import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { getDeviceId, getDeviceLabel } from "@/lib/device";

const AuthCtx = createContext(null);

// We support two parallel sessions: a "teacher/admin" session and a "student"
// session. They use different tokens and different /api/* endpoints. The UI
// surfaces only the active one based on `userType`.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);          // teacher/admin record
  const [student, setStudent] = useState(null);    // student record
  const [userType, setUserType] = useState(null);  // "teacher" | "student" | null
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const check = useCallback(async () => {
    // Try the active type first if known, else try teacher then student
    const tried = [];
    const activeType = localStorage.getItem("sa_user_type");
    const order = activeType ? [activeType, activeType === "student" ? "teacher" : "student"] : ["teacher", "student"];
    for (const t of order) {
      if (tried.includes(t)) continue;
      tried.push(t);
      try {
        if (t === "teacher") {
          const { data } = await api.get("/auth/me");
          setUser(data); setStudent(null); setUserType("teacher");
          setMustChangePassword(!!data.must_change_password);
          setLoading(false); return;
        }
        if (t === "student") {
          const { data } = await api.get("/student/me");
          setStudent(data); setUser(null); setUserType("student");
          setMustChangePassword(false);
          setLoading(false); return;
        }
      } catch (err) {
        if (err?.response?.status !== 401) {
          console.warn(`auth/me (${t}) check failed:`, err);
        }
      }
    }
    setUser(null); setStudent(null); setUserType(null);
    setLoading(false);
  }, []);

  useEffect(() => { check(); }, [check]);

  const login = useCallback(async (phone, password) => {
    try {
      const { data } = await api.post("/auth/login", { phone, password });
      if (data?.token) localStorage.setItem("sa_token", data.token);
      localStorage.setItem("sa_user_type", "teacher");
      localStorage.removeItem("sa_student_token");
      setStudent(null); setUserType("teacher");
      setMustChangePassword(false);
      // Pull the full user model (/auth/me) so flags like is_dev_admin are present
      // immediately — the login payload omits some of them.
      let fullUser = data.user;
      try {
        const me = await api.get("/auth/me");
        fullUser = me.data;
      } catch { /* fall back to login payload */ }
      setUser(fullUser);
      setMustChangePassword(!!data.must_change_password);
      return { ok: true, user: fullUser, userType: "teacher", mustChangePassword: !!data.must_change_password };
    } catch (e) {
      return { ok: false, error: apiErr(e.response?.data?.detail) || e.message };
    }
  }, []);

  const studentLogin = useCallback(async (student_id, password) => {
    try {
      const { data } = await api.post("/student/auth/login", {
        student_id, password,
        device_id: getDeviceId(),
        device_label: getDeviceLabel(),
      });
      if (data?.token) localStorage.setItem("sa_student_token", data.token);
      localStorage.setItem("sa_user_type", "student");
      localStorage.removeItem("sa_token");
      setStudent(data.student); setUser(null); setUserType("student");
      setMustChangePassword(!!data.must_change_password);
      return { ok: true, student: data.student, mustChangePassword: !!data.must_change_password };
    } catch (e) {
      return { ok: false, error: apiErr(e.response?.data?.detail) || e.message };
    }
  }, []);

  const clearMustChangePassword = useCallback(() => setMustChangePassword(false), []);

  // DevAdmin role simulation
  const impersonate = useCallback(async (targetType, targetId) => {
    try {
      const { data } = await api.post("/devadmin/impersonate", { target_type: targetType, target_id: targetId });
      if (data.token_type === "student") {
        localStorage.setItem("sa_student_token", data.token);
        localStorage.setItem("sa_user_type", "student");
        localStorage.removeItem("sa_token");
      } else {
        localStorage.setItem("sa_token", data.token);
        localStorage.setItem("sa_user_type", "teacher");
        localStorage.removeItem("sa_student_token");
      }
      localStorage.removeItem("sa_selected_class");
      localStorage.removeItem("sa_selected_section");
      await check();
      return { ok: true, target: data.target };
    } catch (e) {
      return { ok: false, error: apiErr(e.response?.data?.detail) || e.message };
    }
  }, [check]);

  const stopImpersonating = useCallback(async () => {
    try {
      const { data } = await api.post("/devadmin/stop");
      if (data.token) localStorage.setItem("sa_token", data.token);
      localStorage.setItem("sa_user_type", "teacher");
      localStorage.removeItem("sa_student_token");
      localStorage.removeItem("sa_selected_class");
      localStorage.removeItem("sa_selected_section");
      await check();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiErr(e.response?.data?.detail) || e.message };
    }
  }, [check]);

  const logout = useCallback(async () => {
    const wasStudent = userType === "student";
    try {
      await api.post(wasStudent ? "/student/auth/logout" : "/auth/logout");
    } catch (err) {
      console.warn("logout request failed (continuing with client-side cleanup):", err);
    }
    localStorage.removeItem("sa_token");
    localStorage.removeItem("sa_student_token");
    localStorage.removeItem("sa_user_type");
    localStorage.removeItem("sa_selected_class");
    localStorage.removeItem("sa_selected_section");
    setUser(null); setStudent(null); setUserType(null);
    setMustChangePassword(false);
  }, [userType]);

  const value = useMemo(
    () => ({
      // teacher-side compatibility (most existing pages read .user)
      user,
      // student-side
      student,
      setStudent,    // expose for in-place updates (e.g. profile photo)
      userType,
      mustChangePassword,
      clearMustChangePassword,
      loading,
      login,
      studentLogin,
      logout,
      impersonate,
      stopImpersonating,
      refresh: check,
    }),
    [user, student, userType, mustChangePassword, loading, login, studentLogin, logout, impersonate, stopImpersonating, check, clearMustChangePassword]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
